/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Keeps one bad render from blanking the whole app.
 *
 * React unmounts the entire tree when a render throws and nothing catches it,
 * so the user gets a white page with no message, no navigation and no way back
 * except a manual reload. That is what clicking Call did: the server answered
 * 403 with a structured `detail` object, the page put that object into state,
 * and rendering an object as a React child throws invariant #31.
 *
 * The specific case is fixed at the source (see utils/apiErrors), but ~270
 * call sites across the app share the `detail` pattern, so the class of bug is
 * not going away by fixing them one at a time. This boundary makes the failure
 * mode a readable message inside the normal chrome instead of a blank screen.
 *
 * Deliberately placed INSIDE the router and outside <Routes>: navigation still
 * works, and `resetKey` lets a route change clear the error so the user is not
 * stuck on the boundary after moving somewhere healthy.
 */

import React from "react";
import PropTypes from "prop-types";
import { Alert, AlertTitle, Button } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { frontendLogger } from "utils/logger";

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Goes through the batching logger so the stack reaches the backend the
    // same way other frontend errors do. A white screen previously left no
    // trace anywhere, which is why this one took a screenshot to find.
    try {
      frontendLogger.logError(error, {
        kind: "unhandled_render_error",
        componentStack: info?.componentStack,
        path: window.location?.pathname,
      });
    } catch {
      // Never let the reporter take down the boundary itself.
    }
  }

  componentDidUpdate(prevProps) {
    // Moving to another route clears the error; staying put keeps it.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <MDBox p={4} display="flex" justifyContent="center">
        <MDBox maxWidth="640px" width="100%">
          <Alert severity="error">
            <AlertTitle>This page hit an unexpected error</AlertTitle>
            <MDTypography variant="body2" color="text" mb={2}>
              Nothing you did caused this, and no data was lost. Try again, or move to
              another page and come back.
            </MDTypography>
            <MDTypography variant="caption" color="text" component="div" mb={2}>
              {String(error?.message || error)}
            </MDTypography>
            <Button variant="outlined" size="small" onClick={() => window.location.reload()}>
              Reload
            </Button>
          </Alert>
        </MDBox>
      </MDBox>
    );
  }
}

RouteErrorBoundary.propTypes = {
  children: PropTypes.node,
  // Any value that changes on navigation; a changed key clears the error.
  resetKey: PropTypes.string,
};

RouteErrorBoundary.defaultProps = {
  children: null,
  resetKey: "",
};

export default RouteErrorBoundary;
